import { QRPayload, QRType, SocialPlatform, CryptoCoin } from '@/types/qr';
import { sanitizeUrl } from '@/lib/security/sanitizer';

/**
 * Encodes a structured QRPayload object into a standard QR string format.
 */
export function encodePayload(payload: QRPayload): string {
  switch (payload.type) {
    case 'url':
      return sanitizeUrl(payload.url);

    case 'text':
      return payload.text;

    case 'email': {
      const email = payload.email.trim();
      const params = new URLSearchParams();
      if (payload.subject) params.append('subject', payload.subject);
      if (payload.body) params.append('body', payload.body);
      const query = params.toString();
      return `mailto:${email}${query ? `?${query}` : ''}`;
    }

    case 'phone':
      return `tel:${payload.phone.trim()}`;

    case 'sms': {
      const phone = payload.phone.trim();
      const message = payload.message ? encodeURIComponent(payload.message) : '';
      return message ? `sms:${phone}?body=${message}` : `sms:${phone}`;
    }

    case 'wifi': {
      // Escape special characters in SSID and password: \ ; , : "
      const escapeWifi = (s: string) => s.replace(/([\\;,:"'])/g, '\\$1');
      const ssid = escapeWifi(payload.ssid);
      const pass = payload.password ? escapeWifi(payload.password) : '';
      const enc = payload.encryption || 'WPA';
      const hidden = payload.hidden ? 'true' : 'false';
      return `WIFI:T:${enc};S:${ssid};P:${pass};H:${hidden};;`;
    }

    case 'vcard': {
      const lines = [
        'BEGIN:VCARD',
        'VERSION:3.0',
        `N:${payload.lastName || ''};${payload.firstName || ''};;;`,
        `FN:${[payload.firstName, payload.lastName].filter(Boolean).join(' ')}`
      ];

      if (payload.organization) lines.push(`ORG:${payload.organization}`);
      if (payload.title) lines.push(`TITLE:${payload.title}`);
      if (payload.phone) lines.push(`TEL;TYPE=CELL:${payload.phone}`);
      if (payload.workPhone) lines.push(`TEL;TYPE=WORK:${payload.workPhone}`);
      if (payload.email) lines.push(`EMAIL;TYPE=INTERNET:${payload.email}`);
      if (payload.website) lines.push(`URL:${payload.website}`);
      if (payload.street || payload.city || payload.state || payload.zip || payload.country) {
        lines.push(
          `ADR;TYPE=WORK:;;${payload.street || ''};${payload.city || ''};${payload.state || ''};${payload.zip || ''};${payload.country || ''}`
        );
      }
      if (payload.note) lines.push(`NOTE:${payload.note}`);
      lines.push('END:VCARD');
      return lines.join('\n');
    }

    case 'geo': {
      const lat = payload.latitude;
      const lng = payload.longitude;
      if (payload.query) {
        return `geo:${lat},${lng}?q=${encodeURIComponent(payload.query)}`;
      }
      return `geo:${lat},${lng}`;
    }

    case 'event': {
      const formatIcsDate = (isoString: string) => {
        const d = new Date(isoString);
        return d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
      };

      const start = payload.startDate ? formatIcsDate(payload.startDate) : formatIcsDate(new Date().toISOString());
      const end = payload.endDate ? formatIcsDate(payload.endDate) : start;

      const lines = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//QR Studio//EN',
        'BEGIN:VEVENT',
        `SUMMARY:${payload.title}`,
        `DTSTART:${start}`,
        `DTEND:${end}`
      ];

      if (payload.location) lines.push(`LOCATION:${payload.location}`);
      if (payload.description) lines.push(`DESCRIPTION:${payload.description.replace(/\n/g, '\\n')}`);
      lines.push('END:VEVENT');
      lines.push('END:VCALENDAR');
      return lines.join('\n');
    }

    case 'upi': {
      const params = new URLSearchParams();
      params.append('pa', payload.vpa.trim());
      params.append('pn', payload.payeeName.trim());
      if (payload.amount && Number(payload.amount) > 0) {
        params.append('am', Number(payload.amount).toFixed(2));
      }
      params.append('cu', payload.currency || 'INR');
      if (payload.transactionNote) params.append('tn', payload.transactionNote);
      if (payload.transactionRef) params.append('tr', payload.transactionRef);
      return `upi://pay?${params.toString()}`;
    }

    case 'social': {
      const val = payload.usernameOrUrl.trim().replace(/^@/, '');
      if (val.startsWith('http://') || val.startsWith('https://')) {
        return val;
      }
      const platformUrls: Record<SocialPlatform, string> = {
        instagram: `https://instagram.com/${val}`,
        x: `https://x.com/${val}`,
        facebook: `https://facebook.com/${val}`,
        linkedin: `https://linkedin.com/in/${val}`,
        youtube: `https://youtube.com/@${val}`,
        github: `https://github.com/${val}`,
        tiktok: `https://tiktok.com/@${val}`,
        whatsapp: `https://wa.me/${val.replace(/[^0-9]/g, '')}`,
        telegram: `https://t.me/${val}`
      };
      return platformUrls[payload.platform] || `https://${val}`;
    }

    case 'app':
      if (payload.fallbackUrl) return sanitizeUrl(payload.fallbackUrl);
      if (payload.iosUrl) return sanitizeUrl(payload.iosUrl);
      if (payload.androidUrl) return sanitizeUrl(payload.androidUrl);
      return '';

    case 'crypto': {
      const address = payload.address.trim();
      const params = new URLSearchParams();
      if (payload.amount) params.append('amount', payload.amount);
      if (payload.message) params.append('message', payload.message);
      const query = params.toString();

      const schemes: Record<CryptoCoin, string> = {
        BTC: 'bitcoin',
        ETH: 'ethereum',
        SOL: 'solana',
        USDT: 'ethereum',
        DOGE: 'dogecoin'
      };
      const scheme = schemes[payload.coin] || 'crypto';
      return `${scheme}:${address}${query ? `?${query}` : ''}`;
    }

    case 'custom':
      return payload.content;

    default:
      return '';
  }
}

/**
 * Intelligent parser that identifies the format of a scanned QR code text string.
 */
export function parseDecodedText(raw: string): {
  type: QRType;
  parsedPayload?: Partial<QRPayload>;
} {
  if (!raw) return { type: 'text', parsedPayload: { type: 'text', text: '' } };
  const trimmed = raw.trim();

  // Wi-Fi
  if (trimmed.toUpperCase().startsWith('WIFI:')) {
    const ssidMatch = trimmed.match(/S:([^;]+)/);
    const passMatch = trimmed.match(/P:([^;]+)/);
    const encMatch = trimmed.match(/T:([^;]+)/);
    const hidMatch = trimmed.match(/H:([^;]+)/);

    const unescapeWifi = (s?: string) => (s ? s.replace(/\\([\\;,:"'])/g, '$1') : '');

    return {
      type: 'wifi',
      parsedPayload: {
        type: 'wifi',
        ssid: unescapeWifi(ssidMatch?.[1]) || '',
        password: unescapeWifi(passMatch?.[1]) || '',
        encryption: (encMatch?.[1] as 'WPA' | 'WEP' | 'nopass') || 'WPA',
        hidden: hidMatch?.[1] === 'true'
      }
    };
  }

  // vCard
  if (trimmed.startsWith('BEGIN:VCARD')) {
    const fnMatch = trimmed.match(/FN:(.+)/);
    const telMatch = trimmed.match(/TEL.*:(.+)/);
    const emailMatch = trimmed.match(/EMAIL.*:(.+)/);
    const orgMatch = trimmed.match(/ORG:(.+)/);
    const titleMatch = trimmed.match(/TITLE:(.+)/);
    const urlMatch = trimmed.match(/URL:(.+)/);

    return {
      type: 'vcard',
      parsedPayload: {
        type: 'vcard',
        firstName: fnMatch?.[1]?.trim() || '',
        phone: telMatch?.[1]?.trim() || '',
        email: emailMatch?.[1]?.trim() || '',
        organization: orgMatch?.[1]?.trim() || '',
        title: titleMatch?.[1]?.trim() || '',
        website: urlMatch?.[1]?.trim() || ''
      }
    };
  }

  // UPI
  if (trimmed.toLowerCase().startsWith('upi://pay')) {
    try {
      const url = new URL(trimmed);
      return {
        type: 'upi',
        parsedPayload: {
          type: 'upi',
          vpa: url.searchParams.get('pa') || '',
          payeeName: url.searchParams.get('pn') || '',
          amount: url.searchParams.get('am') || '',
          transactionNote: url.searchParams.get('tn') || '',
          currency: url.searchParams.get('cu') || 'INR'
        }
      };
    } catch {
      return { type: 'upi' };
    }
  }

  // Email (mailto)
  if (trimmed.toLowerCase().startsWith('mailto:')) {
    const mailPart = trimmed.substring(7);
    const [email, query] = mailPart.split('?');
    const params = new URLSearchParams(query || '');
    return {
      type: 'email',
      parsedPayload: {
        type: 'email',
        email: decodeURIComponent(email || ''),
        subject: params.get('subject') || '',
        body: params.get('body') || ''
      }
    };
  }

  // Phone (tel)
  if (trimmed.toLowerCase().startsWith('tel:')) {
    return {
      type: 'phone',
      parsedPayload: {
        type: 'phone',
        phone: trimmed.substring(4)
      }
    };
  }

  // SMS
  if (trimmed.toLowerCase().startsWith('sms:')) {
    const smsPart = trimmed.substring(4);
    const [phone, query] = smsPart.split('?');
    const params = new URLSearchParams(query || '');
    return {
      type: 'sms',
      parsedPayload: {
        type: 'sms',
        phone: phone || '',
        message: params.get('body') || ''
      }
    };
  }

  // Geo Location
  if (trimmed.toLowerCase().startsWith('geo:')) {
    const geoPart = trimmed.substring(4);
    const [coords, query] = geoPart.split('?');
    const [lat, lng] = (coords || '').split(',');
    const params = new URLSearchParams(query || '');
    return {
      type: 'geo',
      parsedPayload: {
        type: 'geo',
        latitude: lat || '0',
        longitude: lng || '0',
        query: params.get('q') || ''
      }
    };
  }

  // iCalendar Event
  if (trimmed.startsWith('BEGIN:VCALENDAR') || trimmed.startsWith('BEGIN:VEVENT')) {
    const summaryMatch = trimmed.match(/SUMMARY:(.+)/);
    const locMatch = trimmed.match(/LOCATION:(.+)/);
    const descMatch = trimmed.match(/DESCRIPTION:(.+)/);
    return {
      type: 'event',
      parsedPayload: {
        type: 'event',
        title: summaryMatch?.[1] || 'Scanned Event',
        location: locMatch?.[1] || '',
        description: descMatch?.[1] || '',
        startDate: new Date().toISOString()
      }
    };
  }

  // Cryptocurrency
  if (/^(bitcoin|ethereum|solana|dogecoin):/i.test(trimmed)) {
    const parts = trimmed.split(':');
    const scheme = parts[0].toLowerCase();
    const [addr, query] = (parts[1] || '').split('?');
    const params = new URLSearchParams(query || '');
    
    let coin: CryptoCoin = 'BTC';
    if (scheme === 'ethereum') coin = 'ETH';
    else if (scheme === 'solana') coin = 'SOL';
    else if (scheme === 'dogecoin') coin = 'DOGE';

    return {
      type: 'crypto',
      parsedPayload: {
        type: 'crypto',
        coin,
        address: addr,
        amount: params.get('amount') || ''
      }
    };
  }

  // URL
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || /^[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i.test(trimmed)) {
    return {
      type: 'url',
      parsedPayload: {
        type: 'url',
        url: trimmed.startsWith('http://') || trimmed.startsWith('https://') ? trimmed : `https://${trimmed}`
      }
    };
  }

  // Default to plain text
  return {
    type: 'text',
    parsedPayload: {
      type: 'text',
      text: trimmed
    }
  };
}
